import classNames from 'classnames';
import {
  PartialQuestionnaire,
  QuestionnaireQuestions,
  Rule,
} from '../../models';
import { findPath } from '../../utils/findPath';

import styles from './ProgressWrapper.module.scss';

export const getQuestionnaireProgress = <
  Questionnaire extends QuestionnaireQuestions,
  GroupId = unknown,
  ExtendedTypes = unknown
>(
  questions: PartialQuestionnaire<Questionnaire, GroupId, ExtendedTypes>,
  answers: Partial<Questionnaire>,
  rule: Rule<Questionnaire>[],
  questionId: keyof Questionnaire,
  questionGroupsMapping: Record<keyof Questionnaire, (keyof Questionnaire)[]>
): { current: number; total: number; percentage: number } => {
  const allQuestionGroups = Object.values(questionGroupsMapping);
  const questionGroup = allQuestionGroups.find((group) =>
    group.includes(questionId)
  );

  const path = findPath(questions, answers, rule);

  const filteredQuestions: (keyof Questionnaire)[] = (
    questionGroup || []
  ).filter((question) => path.includes(question));

  const current = filteredQuestions.indexOf(questionId) + 1;
  const total = filteredQuestions.length;

  return {
    current,
    total,
    percentage: Number(((current / total) * 100).toFixed(0)),
  };
};

export const retrieveCurrentQuestionnaireId = <
  Questionnaire extends QuestionnaireQuestions
>(
  questionId: keyof Questionnaire,
  questionGroupsMapping: Record<keyof Questionnaire, (keyof Questionnaire)[]>
): keyof Questionnaire => {
  const allQuestionnaireIds = Object.keys(
    questionGroupsMapping
  ) as (keyof Questionnaire)[];

  const currentQuestionnaire = allQuestionnaireIds.find((questionnaireId) =>
    questionGroupsMapping[questionnaireId].includes(questionId)
  )!;

  return currentQuestionnaire;
};

type Props<
  Questionnaire extends QuestionnaireQuestions,
  GroupId,
  ExtendedTypes
> = {
  children: React.ReactNode;
  questionnaireAnswers: Partial<Questionnaire>;
  questionId: keyof Partial<Questionnaire>;
  questionnaireIdMapping: Record<keyof Questionnaire, string>;
  questionGroupsMapping: Record<keyof Questionnaire, (keyof Questionnaire)[]>;
  onClick?: (questionId: keyof Questionnaire) => void;
  rules?: Rule<Questionnaire>[];
  questions: PartialQuestionnaire<Questionnaire, GroupId, ExtendedTypes>;
};

export const ProgressWrapper = <
  Questionnaire extends QuestionnaireQuestions,
  GroupId,
  ExtendedTypes
>({
  children,
  questionnaireAnswers,
  rules = [],
  questions,
  questionId,
  onClick,
  questionnaireIdMapping,
  questionGroupsMapping,
}: Props<Questionnaire, GroupId, ExtendedTypes>) => {
  const { current, total, percentage } = getQuestionnaireProgress(
    questions,
    questionnaireAnswers,
    rules,
    questionId,
    questionGroupsMapping
  );

  const path = findPath(questions, questionnaireAnswers, rules);
  const currentQuestionIdx = path.indexOf(questionId);
  const previousQuestionId = path[Math.max(0, currentQuestionIdx - 1)];
  const questionnaireId = retrieveCurrentQuestionnaireId(
    questionId,
    questionGroupsMapping
  );

  if (questionnaireId === undefined) {
    return children;
  }

  return (
    <>
      <div className={`mt48 p-body ${styles.sectionLabelContainer}`}>
        <div className="d-flex">
          {Object.keys(questionnaireIdMapping).map((id) => (
            <div
              className={classNames(
                `p-a p-h4 mr48 c-pointer ${styles.sectionLabel}`,
                {
                  'tc-primary-500': questionnaireId === id,
                  'tc-grey-500': questionnaireId !== id,
                }
              )}
              key={id}
              onClick={() => onClick?.(id)}
            >
              {questionnaireIdMapping[id]}
            </div>
          ))}
        </div>
      </div>
      {children}
      <div className={`w100 ${styles.progressContainer}`}>
        <div className={`p-body ${styles.progressInnerContainer}`}>
          {previousQuestionId ? (
            <button
              className={styles.backBtn}
              onClick={() => onClick?.(previousQuestionId)}
            />
          ) : (
            <button className={styles.backBtn} disabled={true} />
          )}
          <div
            className={`bg-primary-100 w100 ml16 mr16 ${styles.progressBarContainer}`}
          >
            <div
              className={`bg-primary-500 ${styles.progressBar}`}
              style={{
                width: `${percentage}%`,
              }}
            />
          </div>
          <div className="tc-primary-500 p-p--small">{`${current}/${total}`}</div>
        </div>
      </div>
    </>
  );
};
